import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { Saga } from 'redux-saga';
import { call, delay, put, race, select as selectSaga, take } from 'redux-saga/effects';

import { useSagaReducer } from '../../utils/hooks';

import { getEditor } from './__code-container/utils';
import { ROBOT_COMMAND_TYPE, ROBOT_COORD, ROBOT_LINE_NUMBER, ROBOT_RUN_STATUS } from './robot.constants';
import {
    EDITOR_TYPES,
    RobotAttempt,
    RobotCommand,
    RobotCommandRoboMove,
    RobotCommandSetCellColor,
    RobotPosition,
    RobotProblem,
    RobotRunCode,
} from './robot.types';

export interface RobotRunState {
    status: ROBOT_RUN_STATUS;
    position?: RobotCommandRoboMove;
    speed: number[];
    results?: RobotAttempt['markers'];
    lineNumber?: number;
    coloredCells: RobotCommandSetCellColor[];
    key?: number;
    crash?: RobotPosition;
    showOnboarding: boolean;
}

const initialState: RobotRunState = {
    status: ROBOT_RUN_STATUS.IDLE,
    coloredCells: [],
    speed: [1, 2, 10, 0.5],
    showOnboarding: false,
};

const slice = createSlice({
    name: 'robotRun',
    initialState: initialState,
    reducers: {
        reset(state) {
            state.position = undefined;
            state.status = ROBOT_RUN_STATUS.IDLE;
            state.lineNumber = undefined;
            state.coloredCells = [];
            state.key = (state.key || 0) + 1;
            state.crash = undefined;
        },
        shiftSpeed(state) {
            state.speed.push(state.speed.shift()!);
        },
        status(state, action: PayloadAction<Pick<RobotRunState, 'status'>>) {
            state.status = action.payload.status;
        },
        lineNumber(state, action: PayloadAction<Pick<RobotRunState, 'lineNumber'>>) {
            state.lineNumber = action.payload.lineNumber;
        },
        results(state, action: PayloadAction<Pick<RobotRunState, 'results'>>) {
            state.results = action.payload.results;
        },
        showOnboarding(state, action: PayloadAction<Pick<RobotRunState, 'showOnboarding'>>) {
            state.showOnboarding = action.payload.showOnboarding;
        },
        command(state, action: PayloadAction<{ command: RobotCommand }>) {
            const { command } = action.payload;

            switch (command.type) {
                case ROBOT_COMMAND_TYPE.ROBO_MOVE:
                    if (command.crashed && state.position) {
                        state.crash = {
                            [ROBOT_COORD.HORIZONTAL]:
                                command[ROBOT_COORD.HORIZONTAL] - state.position[ROBOT_COORD.HORIZONTAL],
                            [ROBOT_COORD.VERTICAL]:
                                command[ROBOT_COORD.VERTICAL] - state.position[ROBOT_COORD.VERTICAL],
                        };
                        break;
                    }

                    state.position = command;
                    break;
                case ROBOT_COMMAND_TYPE.SET_CELL_COLOR:
                    state.coloredCells.push(command);
                    break;
                default:
                // no default
            }
        },
        setState: (state, action: PayloadAction<RobotRunState>) => Object.assign(state, action.payload),
        play: () => undefined, // action for saga
        pause: () => undefined, // action for saga
        step: () => undefined, // action for saga
        stop: () => undefined, // action for saga
    },
});

const { actions, reducer } = slice;

const select = selectSaga as <T>(selector: (state: RobotRunState) => T) => T;

function* takePlayStep() {
    const action: Action = yield take([actions.play, actions.step]);
    yield put(
        actions.status({
            status: actions.play.match(action) ? ROBOT_RUN_STATUS.PLAY : ROBOT_RUN_STATUS.STEP,
        })
    );
}

function* waitNextStep() {
    const isPlaying: boolean = yield select((s) => s.status === ROBOT_RUN_STATUS.PLAY);

    if (isPlaying) {
        const robotSpeed: number = yield select((s) => s.speed[0]);
        // Скорость для тестов
        const speed = window.__testRobotSpeed || robotSpeed;
        const { pause, step } = yield race({
            delay: delay(500 / speed),
            pause: take(actions.pause),
            step: take(actions.step),
        });
        if (pause) {
            yield put(actions.status({ status: ROBOT_RUN_STATUS.STEP }));
            yield takePlayStep();
        }
        if (step) {
            yield put(actions.status({ status: ROBOT_RUN_STATUS.STEP }));
        }
    } else {
        yield takePlayStep();
    }
}

export function useRobotRun(
    sample: number,
    runCode: RobotRunCode,
    parameters?: {
        problem: RobotProblem;
        submitAnswer?: () => void;
    }
) {
    const saga = useCallback(
        function* () {
            while (true) {
                yield put(actions.reset());
                const action: Action = yield take([actions.play, actions.step]);
                yield put(actions.status({ status: ROBOT_RUN_STATUS.LOADING }));
                try {
                    const results: RobotAttempt['markers'] = yield call(runCode);
                    yield put(actions.results({ results }));
                    yield put(
                        actions.status({
                            status: actions.play.match(action) ? ROBOT_RUN_STATUS.PLAY : ROBOT_RUN_STATUS.STEP,
                        })
                    );
                    const commands = results.result?.[sample].check_details.sample;
                    if (commands && commands.length) {
                        const raceResult: Partial<{
                            runCode: Record<string, never> | Record<'isCodeLightV2AnswerCorrect', true>;
                            stop: Record<string, never>;
                        }> = yield race({
                            runCode: call(function* () {
                                // let s = yield select(s => s); // fast forward
                                for (const command of commands) {
                                    if (ROBOT_LINE_NUMBER in command) {
                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore не понимает in
                                        const lineNumber = command[ROBOT_LINE_NUMBER];
                                        yield put(actions.lineNumber({ lineNumber }));
                                        yield waitNextStep();
                                    }
                                    // s = reducer(s, actions.command({ command })); // fast forward
                                    yield put(actions.command({ command }));
                                }
                                // yield put(actions.setState({ ...s, status: ROBOT_RUN_STATUS.FINISH })); // fast forward
                                yield put(actions.status({ status: ROBOT_RUN_STATUS.FINISH }));

                                if (parameters?.submitAnswer) {
                                    const isSuccessAnswer = results.result[sample].answer_status;
                                    const isCodeLightV2 =
                                        getEditor(parameters.problem.markup).editor === EDITOR_TYPES.CODE_LIGHT_V2;

                                    if (isCodeLightV2 && isSuccessAnswer) {
                                        yield call(parameters.submitAnswer);
                                        return { isCodeLightV2AnswerCorrect: true };
                                    }
                                }

                                yield take(actions.stop);
                                return {};
                            }),
                            stop: take(actions.stop),
                        });

                        if (raceResult.runCode?.isCodeLightV2AnswerCorrect) {
                            return;
                        }
                    }
                } catch (e) {
                    // сетевая ошибка обрабатывается в useRunCode
                }
            }
        },
        [sample, runCode]
    );

    const [state, dispatch] = useSagaReducer(reducer, initialState, saga as Saga);

    return [state, dispatch] as const;
}

const exportActions = {
    play: actions.play,
    pause: actions.pause,
    step: actions.step,
    stop: actions.stop,
    results: actions.results,
    shiftSpeed: actions.shiftSpeed,
    showOnboarding: actions.showOnboarding,
};

export { exportActions as actions };
